// app/src/lib/api.ts — thin fetch wrapper around the Worker API.

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) { super(message); }
}
export class OfflineError extends Error {
  constructor() { super("offline"); }
}

// Tracks in-flight API requests so a global top progress bar can show activity
// across the whole app. Emits an "epos:loading" event with the active count.
let active = 0;
function setActive(n: number) {
  active = n < 0 ? 0 : n;
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("epos:loading", { detail: active }));
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  setActive(active + 1);
  try {
    let res: Response;
    try {
      res = await fetch(`/api${path}`, {
        method,
        headers: body ? { "Content-Type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        credentials: "include",
      });
    } catch {
      throw new OfflineError(); // network failure -> treat as offline
    }
    const text = await res.text();
    // Cloudflare edge failures (520–526, 530, error 1101) and some proxies return an
    // HTML page instead of JSON — never let JSON.parse turn that into a cryptic crash.
    let data: any = null;
    let parsedOk = true;
    if (text) { try { data = JSON.parse(text); } catch { parsedOk = false; } }
    if (!res.ok) {
      const isEdge = (res.status >= 520 && res.status <= 530)
        || (!parsedOk && res.status >= 500)
        || (res.headers.get("content-type") ?? "").includes("text/html");
      if (isEdge) throw new ApiError(res.status, "Network edge problem — retry in a few seconds", "edge");
      if (res.status === 429) throw new ApiError(429, data?.error ?? "Too many requests — wait a moment and retry", "rate_limited");
      // A 401 from a protected endpoint means the server ended the session
      // (idle timeout, device/IP change, revoked) — signal the app to redirect.
      if (res.status === 401 && typeof window !== "undefined"
          && !path.startsWith("/auth/login") && !path.startsWith("/auth/verify-otp")) {
        window.dispatchEvent(new Event("epos:session-expired"));
      }
      throw new ApiError(res.status, data?.error ?? res.statusText, data?.code);
    }
    if (!parsedOk) throw new ApiError(502, "Bad response from server — retry", "edge");
    return data as T;
  } finally {
    setActive(active - 1);
  }
}

export const api = {
  get: <T>(p: string) => getWithRetry<T>(p),
  post: <T>(p: string, b?: unknown) => request<T>("POST", p, b),
  patch: <T>(p: string, b?: unknown) => request<T>("PATCH", p, b),
  put: <T>(p: string, b?: unknown) => request<T>("PUT", p, b),
  del: <T>(p: string) => request<T>("DELETE", p),
};

// Reads are idempotent, so a single quiet retry on a transient server error
// (e.g. a momentary D1/Singapore hiccup) turns a one-off "Internal error" into a
// silent recovery. We deliberately do NOT retry writes (POST/PATCH/PUT/DELETE) —
// they could apply twice — nor 4xx, which are real client-side problems.
async function getWithRetry<T>(p: string): Promise<T> {
  try {
    return await request<T>("GET", p);
  } catch (e) {
    if (e instanceof ApiError && e.status >= 500) {
      await new Promise((r) => setTimeout(r, 400));
      return request<T>("GET", p);
    }
    throw e;
  }
}
